import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { MicrophoneAvailability } from 'src/app/models/microphone-availability.model';
import { MicrophoneCheckStatus } from 'src/app/models/microphone-check.model';

/**
 * Knows, before a call is placed, whether this browser can give it a microphone.
 *
 * The pre-call check in TwilioService only runs when the care manager presses Call, so a blocked
 * permission or an unplugged headset was discovered at the worst moment, with the patient's number
 * already chosen. This watches for both from the moment the Device is registered, and again
 * whenever the permission or the device list changes.
 *
 * Deliberately never calls `getUserMedia`: that would light the recording indicator, or pop the
 * browser's permission prompt, on a page where nobody is calling. It only reads what the browser
 * already exposes (`permissions.query` and `enumerateDevices`). Where those say nothing — Safari,
 * older Firefox — the pre-call check's verdict is fed back in through {@link recordCheckResult}.
 */
@Injectable({ providedIn: 'root' })
export class MicrophoneAvailabilityService {
  readonly availability$: Observable<MicrophoneAvailability>;

  private readonly state = new BehaviorSubject<MicrophoneAvailability>('unknown');
  private permissionStatus: PermissionStatus | null = null;
  private started = false;
  /**
   * A refusal the pre-call check saw while the Permissions API could not tell. Kept until a check
   * succeeds, because without that API nothing else will ever report the refusal lifted.
   */
  private deniedByCheck = false;
  private readonly onChange: () => void;

  constructor(private zone: NgZone) {
    this.availability$ = this.state.pipe(distinctUntilChanged());
    // Browser events fire outside Angular; re-entering the zone is what makes the UI react.
    this.onChange = () => this.zone.run(() => void this.refresh());
  }

  get current(): MicrophoneAvailability {
    return this.state.value;
  }

  /** Starts watching. Idempotent: the Device can be initialised more than once per page. */
  async start(): Promise<MicrophoneAvailability> {
    if (!this.started) {
      this.started = true;
      navigator.mediaDevices?.addEventListener?.('devicechange', this.onChange);
      this.permissionStatus = await this.queryPermission();
      if (this.permissionStatus) {
        this.permissionStatus.onchange = this.onChange;
      }
    }
    return this.refresh();
  }

  /** Stops watching, on sign-out. The next user starts from `unknown`. */
  stop(): void {
    navigator.mediaDevices?.removeEventListener?.('devicechange', this.onChange);
    if (this.permissionStatus) {
      this.permissionStatus.onchange = null;
    }
    this.permissionStatus = null;
    this.started = false;
    this.deniedByCheck = false;
    this.state.next('unknown');
  }

  /** Re-reads the permission and the device list. Never throws. */
  async refresh(): Promise<MicrophoneAvailability> {
    let next: MicrophoneAvailability;
    try {
      next = await this.evaluate();
    } catch {
      // A browser that cannot answer must not be able to stop calls.
      next = 'unknown';
    }
    this.state.next(next);
    return next;
  }

  /**
   * What "Try again" runs: {@link refresh}, after re-asking for the microphone when a refusal is only
   * known from the pre-call check.
   *
   * Without the Permissions API (Safari, older Firefox) nothing else can ever lift that refusal, and
   * the Call button stays disabled while it stands — so re-reading the same flag left the care manager
   * stuck until a reload (PR review). Opening the microphone is the only way to learn it was allowed
   * again. Only on this path: it is a click, so a browser prompt is expected, and with the Permissions
   * API the browser itself reports the change.
   */
  async recheck(): Promise<MicrophoneAvailability> {
    if (this.deniedByCheck && navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
        this.deniedByCheck = false;
      } catch {
        // Still refused, or no device: refresh reports whichever applies.
      }
    }
    return this.refresh();
  }

  /**
   * Feeds back what the pre-call check found by actually opening the microphone. That is the only
   * source of truth where the Permissions API is missing, and the quickest one everywhere.
   */
  recordCheckResult(status: MicrophoneCheckStatus): void {
    if (status === 'ok') {
      this.deniedByCheck = false;
      this.state.next('available');
    } else if (status === 'permission-denied') {
      this.deniedByCheck = !this.permissionStatus;
      this.state.next('permission-denied');
    } else if (status === 'no-device') {
      this.state.next('no-device');
    }
  }

  private async evaluate(): Promise<MicrophoneAvailability> {
    if (this.permissionStatus?.state === 'denied' || this.deniedByCheck) {
      return 'permission-denied';
    }
    if (!navigator.mediaDevices?.enumerateDevices) {
      return 'unknown';
    }
    // Before access is granted the browser still lists one entry per input, only without a label,
    // so an empty list does mean "no microphone", not "not allowed to see it".
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some((device) => device.kind === 'audioinput') ? 'available' : 'no-device';
  }

  private async queryPermission(): Promise<PermissionStatus | null> {
    try {
      // 'microphone' is not in TypeScript's PermissionName for this lib version, and Firefox throws
      // a TypeError on it; either way the answer is "cannot tell".
      return (await navigator.permissions?.query({ name: 'microphone' as PermissionName })) ?? null;
    } catch {
      return null;
    }
  }
}
